/**
 * Safe date formatting utilities
 * Handles null, undefined, empty strings, and invalid date values
 */

/**
 * Format a date safely, returning fallback for invalid values
 * @param {string|number|Date|null|undefined} dateValue - Input date value
 * @param {string} fallback - String to return if date is invalid (default: '-')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string or fallback
 */
export function formatDateSafe(dateValue, fallback = '-', options = {}) {
  // Handle null/undefined/empty
  if (!dateValue && dateValue !== 0) return fallback;

  // Handle empty string
  if (dateValue === '') return fallback;

  // Parse the date
  const date = dateValue instanceof Date
    ? dateValue
    : new Date(dateValue);

  // Check if valid
  if (isNaN(date.getTime())) return fallback;

  // Format with defaults
  const defaultOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  };

  return date.toLocaleDateString('en-GB', { ...defaultOptions, ...options });
}

/**
 * Format datetime safely
 */
export function formatDateTimeSafe(dateValue, fallback = '-') {
  if (!dateValue && dateValue !== 0) return fallback;
  if (dateValue === '') return fallback;

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (isNaN(date.getTime())) return fallback;

  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Check if a date value is valid
 */
export function isValidDate(dateValue) {
  if (!dateValue && dateValue !== 0) return false;
  if (dateValue === '') return false;
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  return !isNaN(date.getTime());
}
